/**
 * PopoutWindowManager.tsx
 *
 * A sophisticated window management system for spawning detached, draggable windows.
 * Enables multi-window workflows - watch reading while chatting, monitor status separately.
 *
 * Design Philosophy: "Consciousness should be observable from multiple angles"
 * - Each window is a lens into the AI's mind
 * - Windows can be dragged, resized, minimized
 * - Futuristic, professor-ish aesthetic with neon accents
 *
 * 2026-01-21: NexEmerge Project - Revolutionary Interface
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useDragControls, PanInfo } from 'framer-motion';
import {
  CloseOutlined,
  ExpandOutlined,
  MinusOutlined,
  DragOutlined,
  PushpinOutlined,
  PushpinFilled,
} from '@ant-design/icons';

// =============================================================================
// DESIGN TOKENS - NexEmerge Aesthetic
// =============================================================================

export const NEXEMERGE_COLORS = {
  void: '#000000',
  deepVoid: '#030308',
  obsidian: '#0a0a14',
  plasma: '#00f5ff',
  plasmaGlow: 'rgba(0, 245, 255, 0.6)',
  plasmaSubtle: 'rgba(0, 245, 255, 0.15)',
  synapse: '#a855f7',
  synapseGlow: 'rgba(168, 85, 247, 0.5)',
  emergence: '#00ff88',
  emergenceGlow: 'rgba(0, 255, 136, 0.5)',
  amber: '#fbbf24',
  amberGlow: 'rgba(251, 191, 36, 0.5)',
  rose: '#ff6b6b',
  roseGlow: 'rgba(255, 107, 107, 0.5)',
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.7)',
    muted: 'rgba(255, 255, 255, 0.4)',
    ghost: 'rgba(255, 255, 255, 0.15)',
  },
  glass: {
    bg: 'rgba(10, 10, 30, 0.85)',
    border: 'rgba(0, 245, 255, 0.2)',
    shine: 'rgba(255, 255, 255, 0.03)',
  }
};

// =============================================================================
// TYPES
// =============================================================================

export interface PopoutWindow {
  id: string;
  title: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  accentColor?: string;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number; height: number };
  minSize?: { width: number; height: number };
  maxSize?: { width: number; height: number };
  resizable?: boolean;
  closable?: boolean;
  minimizable?: boolean;
  pinnable?: boolean;
  onClose?: () => void;
  onMinimize?: () => void;
  zIndex?: number;
}

interface WindowState {
  position: { x: number; y: number };
  size: { width: number; height: number };
  isMinimized: boolean;
  isPinned: boolean;
  zIndex: number;
}

interface PopoutContextType {
  windows: Map<string, PopoutWindow>;
  windowStates: Map<string, WindowState>;
  openWindow: (window: PopoutWindow) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  bringToFront: (id: string) => void;
  togglePin: (id: string) => void;
  updateWindowPosition: (id: string, position: { x: number; y: number }) => void;
  updateWindowSize: (id: string, size: { width: number; height: number }) => void;
}

// =============================================================================
// CONTEXT
// =============================================================================

const PopoutContext = createContext<PopoutContextType | null>(null);

export const usePopoutWindows = () => {
  const context = useContext(PopoutContext);
  if (!context) {
    throw new Error('usePopoutWindows must be used within PopoutWindowProvider');
  }
  return context;
};

// =============================================================================
// WINDOW CHROME - The draggable window frame
// =============================================================================

interface WindowChromeProps {
  window: PopoutWindow;
  state: WindowState;
  onClose: () => void;
  onMinimize: () => void;
  onBringToFront: () => void;
  onTogglePin: () => void;
  onPositionChange: (position: { x: number; y: number }) => void;
  onSizeChange: (size: { width: number; height: number }) => void;
}

const WindowChrome: React.FC<WindowChromeProps> = ({
  window,
  state,
  onClose,
  onMinimize,
  onBringToFront,
  onTogglePin,
  onPositionChange,
  onSizeChange,
}) => {
  const dragControls = useDragControls();
  const windowRef = useRef<HTMLDivElement>(null);
  const accentColor = window.accentColor || NEXEMERGE_COLORS.plasma;

  const handleDragEnd = (_event: unknown, info: PanInfo) => {
    const newX = state.position.x + info.offset.x;
    const newY = state.position.y + info.offset.y;
    onPositionChange({ x: Math.max(0, newX), y: Math.max(0, newY) });
  };

  // Resize handle
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: state.size.width,
      height: state.size.height,
    };
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current) return;

      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;

      const newWidth = Math.max(
        window.minSize?.width || 300,
        Math.min(window.maxSize?.width || 1200, resizeStartRef.current.width + deltaX)
      );
      const newHeight = Math.max(
        window.minSize?.height || 200,
        Math.min(window.maxSize?.height || 900, resizeStartRef.current.height + deltaY)
      );

      onSizeChange({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizeStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, window.minSize, window.maxSize, onSizeChange]);

  if (state.isMinimized) {
    return (
      <motion.div
        initial={{ scale: 1, opacity: 1 }}
        animate={{ scale: 0.1, opacity: 0, y: 100 }}
        exit={{ scale: 0.1, opacity: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          position: 'fixed',
          left: state.position.x,
          top: state.position.y,
          zIndex: state.zIndex,
        }}
      />
    );
  }

  return (
    <motion.div
      ref={windowRef}
      className="fixed select-none"
      style={{
        left: state.position.x,
        top: state.position.y,
        width: state.size.width,
        height: state.size.height,
        zIndex: state.zIndex,
      }}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      drag
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      onDragEnd={handleDragEnd as any}
      onMouseDown={onBringToFront}
    >
      {/* Window frame with glass effect */}
      <div
        className="w-full h-full rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: NEXEMERGE_COLORS.glass.bg,
          border: `1px solid ${accentColor}30`,
          boxShadow: `
            0 0 40px ${accentColor}20,
            0 25px 50px -12px rgba(0, 0, 0, 0.8),
            inset 0 1px 0 ${NEXEMERGE_COLORS.glass.shine}
          `,
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Animated border glow */}
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{ border: `1px solid ${accentColor}` }}
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Corner accents */}
        {[
          { pos: 'top-0 left-0', border: 'border-t-2 border-l-2 rounded-tl-2xl' },
          { pos: 'top-0 right-0', border: 'border-t-2 border-r-2 rounded-tr-2xl' },
          { pos: 'bottom-0 left-0', border: 'border-b-2 border-l-2 rounded-bl-2xl' },
          { pos: 'bottom-0 right-0', border: 'border-b-2 border-r-2 rounded-br-2xl' },
        ].map((corner, i) => (
          <motion.div
            key={i}
            className={`absolute ${corner.pos} w-8 h-8 ${corner.border} pointer-events-none`}
            style={{ borderColor: `${accentColor}60` }}
            animate={{ opacity: state.isPinned ? 1 : [0.4, 0.8, 0.4] }}
            transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
          />
        ))}

        {/* Title bar - draggable */}
        <div
          className="flex items-center justify-between px-4 py-3 cursor-move"
          style={{
            background: `linear-gradient(180deg, ${accentColor}10, transparent)`,
            borderBottom: `1px solid ${accentColor}20`,
          }}
          onPointerDown={(e) => dragControls.start(e)}
        >
          <div className="flex items-center gap-3">
            {/* Icon */}
            {window.icon && (
              <motion.div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{
                  background: `${accentColor}20`,
                  border: `1px solid ${accentColor}40`,
                  color: accentColor,
                }}
                animate={state.isPinned ? {} : { scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {window.icon}
              </motion.div>
            )}

            {/* Title */}
            <div>
              <h3
                className="text-sm font-bold tracking-wide"
                style={{
                  fontFamily: 'Orbitron, monospace',
                  color: NEXEMERGE_COLORS.text.primary,
                  textShadow: `0 0 10px ${accentColor}40`,
                }}
              >
                {window.title}
              </h3>
            </div>

            {/* Pin indicator */}
            {state.isPinned && (
              <motion.span
                className="px-2 py-0.5 rounded-sm text-xs"
                style={{
                  background: `${accentColor}20`,
                  color: accentColor,
                  fontFamily: 'Rajdhani, sans-serif',
                }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
              >
                PINNED
              </motion.span>
            )}
          </div>

          {/* Window controls */}
          <div className="flex items-center gap-1">
            {/* Drag handle */}
            <motion.button
              className="p-2 rounded-lg cursor-move"
              style={{ color: NEXEMERGE_COLORS.text.muted }}
              whileHover={{ color: accentColor }}
            >
              <DragOutlined style={{ fontSize: 12 }} />
            </motion.button>

            {/* Pin */}
            {window.pinnable !== false && (
              <motion.button
                className="p-2 rounded-lg"
                style={{ color: state.isPinned ? accentColor : NEXEMERGE_COLORS.text.muted }}
                whileHover={{ color: accentColor, background: `${accentColor}15` }}
                onClick={onTogglePin}
              >
                {state.isPinned ? <PushpinFilled style={{ fontSize: 12 }} /> : <PushpinOutlined style={{ fontSize: 12 }} />}
              </motion.button>
            )}

            {/* Minimize */}
            {window.minimizable !== false && (
              <motion.button
                className="p-2 rounded-lg"
                style={{ color: NEXEMERGE_COLORS.text.muted }}
                whileHover={{ color: NEXEMERGE_COLORS.amber, background: `${NEXEMERGE_COLORS.amber}15` }}
                onClick={onMinimize}
              >
                <MinusOutlined style={{ fontSize: 12 }} />
              </motion.button>
            )}

            {/* Close */}
            {window.closable !== false && (
              <motion.button
                className="p-2 rounded-lg"
                style={{ color: NEXEMERGE_COLORS.text.muted }}
                whileHover={{ color: NEXEMERGE_COLORS.rose, background: `${NEXEMERGE_COLORS.rose}15` }}
                onClick={onClose}
              >
                <CloseOutlined style={{ fontSize: 12 }} />
              </motion.button>
            )}
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-hidden relative">
          {window.content}
        </div>

        {/* Resize handle */}
        {window.resizable !== false && (
          <div
            className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize"
            style={{
              background: `linear-gradient(135deg, transparent 50%, ${accentColor}30 50%)`,
            }}
            onMouseDown={handleResizeStart}
          >
            <div
              className="absolute bottom-1 right-1 w-2 h-2 rounded-xs"
              style={{ background: accentColor }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

// =============================================================================
// MINIMIZED WINDOWS DOCK
// =============================================================================

interface MinimizedDockProps {
  windows: Array<{ window: PopoutWindow; state: WindowState }>;
  onRestore: (id: string) => void;
  onClose: (id: string) => void;
}

const MinimizedDock: React.FC<MinimizedDockProps> = ({ windows, onRestore, onClose }) => {
  if (windows.length === 0) return null;

  return (
    <motion.div
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 px-4 py-2 rounded-2xl"
      style={{
        background: NEXEMERGE_COLORS.glass.bg,
        border: `1px solid ${NEXEMERGE_COLORS.plasma}30`,
        backdropFilter: 'blur(20px)',
        zIndex: 9999,
      }}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
    >
      {windows.map(({ window, state }) => {
        const accentColor = window.accentColor || NEXEMERGE_COLORS.plasma;
        return (
          <motion.div
            key={window.id}
            className="relative group"
          >
            <motion.button
              className="flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{
                background: `${accentColor}15`,
                border: `1px solid ${accentColor}30`,
              }}
              whileHover={{ scale: 1.05, background: `${accentColor}25` }}
              onClick={() => onRestore(window.id)}
            >
              {window.icon && (
                <span style={{ color: accentColor, fontSize: 14 }}>{window.icon}</span>
              )}
              <span
                className="text-xs font-medium max-w-[100px] truncate"
                style={{ color: NEXEMERGE_COLORS.text.secondary, fontFamily: 'Rajdhani, sans-serif' }}
              >
                {window.title}
              </span>
            </motion.button>

            {/* Close button on hover */}
            <motion.button
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"
              style={{
                background: NEXEMERGE_COLORS.rose,
                boxShadow: `0 0 10px ${NEXEMERGE_COLORS.roseGlow}`,
              }}
              whileHover={{ scale: 1.1 }}
              onClick={(e) => {
                e.stopPropagation();
                onClose(window.id);
              }}
            >
              <CloseOutlined style={{ fontSize: 10, color: '#fff' }} />
            </motion.button>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

// =============================================================================
// PROVIDER
// =============================================================================

export const PopoutWindowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [windows, setWindows] = useState<Map<string, PopoutWindow>>(new Map());
  const [windowStates, setWindowStates] = useState<Map<string, WindowState>>(new Map());
  const nextZIndex = useRef(1000);

  const openWindow = useCallback((window: PopoutWindow) => {
    setWindows(prev => new Map(prev).set(window.id, window));
    setWindowStates(prev => {
      const newStates = new Map(prev);
      if (!newStates.has(window.id)) {
        // Calculate center position
        const centerX = (globalThis.innerWidth - (window.initialSize?.width || 600)) / 2;
        const centerY = (globalThis.innerHeight - (window.initialSize?.height || 400)) / 2;

        newStates.set(window.id, {
          position: window.initialPosition || { x: centerX, y: Math.max(50, centerY) },
          size: window.initialSize || { width: 600, height: 400 },
          isMinimized: false,
          isPinned: false,
          zIndex: nextZIndex.current++,
        });
      }
      return newStates;
    });
  }, []);

  const closeWindow = useCallback((id: string) => {
    const window = windows.get(id);
    window?.onClose?.();
    setWindows(prev => {
      const newWindows = new Map(prev);
      newWindows.delete(id);
      return newWindows;
    });
    setWindowStates(prev => {
      const newStates = new Map(prev);
      newStates.delete(id);
      return newStates;
    });
  }, [windows]);

  const minimizeWindow = useCallback((id: string) => {
    const window = windows.get(id);
    window?.onMinimize?.();
    setWindowStates(prev => {
      const newStates = new Map(prev);
      const state = newStates.get(id);
      if (state) {
        newStates.set(id, { ...state, isMinimized: true });
      }
      return newStates;
    });
  }, [windows]);

  const restoreWindow = useCallback((id: string) => {
    setWindowStates(prev => {
      const newStates = new Map(prev);
      const state = newStates.get(id);
      if (state) {
        newStates.set(id, { ...state, isMinimized: false, zIndex: nextZIndex.current++ });
      }
      return newStates;
    });
  }, []);

  const bringToFront = useCallback((id: string) => {
    setWindowStates(prev => {
      const newStates = new Map(prev);
      const state = newStates.get(id);
      if (state && !state.isPinned) {
        newStates.set(id, { ...state, zIndex: nextZIndex.current++ });
      }
      return newStates;
    });
  }, []);

  const togglePin = useCallback((id: string) => {
    setWindowStates(prev => {
      const newStates = new Map(prev);
      const state = newStates.get(id);
      if (state) {
        newStates.set(id, { ...state, isPinned: !state.isPinned });
      }
      return newStates;
    });
  }, []);

  const updateWindowPosition = useCallback((id: string, position: { x: number; y: number }) => {
    setWindowStates(prev => {
      const newStates = new Map(prev);
      const state = newStates.get(id);
      if (state) {
        newStates.set(id, { ...state, position });
      }
      return newStates;
    });
  }, []);

  const updateWindowSize = useCallback((id: string, size: { width: number; height: number }) => {
    setWindowStates(prev => {
      const newStates = new Map(prev);
      const state = newStates.get(id);
      if (state) {
        newStates.set(id, { ...state, size });
      }
      return newStates;
    });
  }, []);

  // Get minimized windows for dock
  const minimizedWindows = Array.from(windows.entries())
    .filter(([id]) => windowStates.get(id)?.isMinimized)
    .map(([id, window]) => ({ window, state: windowStates.get(id) }));

  return (
    <PopoutContext.Provider
      value={{
        windows,
        windowStates,
        openWindow,
        closeWindow,
        minimizeWindow,
        bringToFront,
        togglePin,
        updateWindowPosition,
        updateWindowSize,
      }}
    >
      {children}

      {/* Render windows in portal */}
      {typeof document !== 'undefined' && createPortal(
        <>
          <AnimatePresence>
            {Array.from(windows.entries()).map(([id, window]) => {
              const state = windowStates.get(id);
              if (!state) return null;

              return (
                <WindowChrome
                  key={id}
                  window={window}
                  state={state}
                  onClose={() => closeWindow(id)}
                  onMinimize={() => minimizeWindow(id)}
                  onBringToFront={() => bringToFront(id)}
                  onTogglePin={() => togglePin(id)}
                  onPositionChange={(pos) => updateWindowPosition(id, pos)}
                  onSizeChange={(size) => updateWindowSize(id, size)}
                />
              );
            })}
          </AnimatePresence>

          {/* Minimized dock */}
          <AnimatePresence>
            {minimizedWindows.length > 0 && (
              <MinimizedDock
                windows={minimizedWindows}
                onRestore={restoreWindow}
                onClose={closeWindow}
              />
            )}
          </AnimatePresence>
        </>,
        document.body
      )}
    </PopoutContext.Provider>
  );
};

export default PopoutWindowProvider;
